import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

const Popover = ({ trigger, children, position = 'auto', className, classNameContainer }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [placement, setPlacement] = useState({ top: true, leftEdge: true })
    const triggerRef = useRef(null)
    const popoverRef = useRef(null)

    const toggleOpen = () => setIsOpen((prev) => !prev)

    const handleClickOutside = (e) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target) && !triggerRef.current.contains(e.target)) {
            setIsOpen(false)
        }
    }

    const positionPopover = () => {
        if (!triggerRef.current || !popoverRef.current) return

        const triggerRect = triggerRef.current.getBoundingClientRect()
        const popoverRect = popoverRef.current.getBoundingClientRect()
        const spacing = 8

        // Determine vertical placement
        const fitsBelow = window.innerHeight - triggerRect.bottom > popoverRect.height + spacing
        const fitsAbove = triggerRect.top > popoverRect.height + spacing
        const placeBelow = position === 'bottom' || (position === 'auto' && fitsBelow)
        const placeAbove = position === 'top' || (position === 'auto' && !fitsBelow && fitsAbove)

        // Determine horizontal placement
        const fitsRight = triggerRect.left + popoverRect.width < window.innerWidth - spacing
        const fitsLeft = triggerRect.right - popoverRect.width > spacing
        const alignLeft = fitsRight || (!fitsRight && !fitsLeft) // default fallback to left

        setPlacement({
            top: placeAbove ? false : true, // default below if both fail
            leftEdge: alignLeft,
        })
    }

    useEffect(() => {
        if (isOpen) positionPopover()
    }, [isOpen])

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('resize', positionPopover)
        window.addEventListener('scroll', positionPopover, true)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('resize', positionPopover)
            window.removeEventListener('scroll', positionPopover, true)
        }
    }, [])

    return (
        <div ref={triggerRef} className={clsx('relative inline-block', classNameContainer)}>
            <div onClick={toggleOpen} className='cursor-pointer'>
                {trigger}
            </div>

            {isOpen && (
                <div
                    ref={popoverRef}
                    onClick={() => setIsOpen(false)}
                    className={clsx(
                        'absolute z-50 min-w-[150px] rounded-lg shadow-lg bg-white border border-neutral-20 p-2 transition-all duration-200 ease-out opacity-0 animate-popover-in',
                        {
                            'top-full mt-1': placement.top,
                            'bottom-full mb-1': !placement.top,
                            'left-0': placement.leftEdge,
                            'right-0': !placement.leftEdge,
                        },
                        className,
                    )}
                >
                    {children}
                </div>
            )}

            {/* Animation */}
            <style jsx='true'>{`
                @keyframes popover-in {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(${placement.top ? '-16px' : '16px'});
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                .animate-popover-in {
                    animation: popover-in 0.15s ease-out forwards;
                }
            `}</style>
        </div>
    )
}

export default Popover
